import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useModuleVisibility } from "../hooks/useModuleVisibility";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, Empty } from "../components/ui-helpers";
import { DataResidencyNote } from "../components/DataResidencyNote";
import { FamilyToggle } from "../components/FamilyToggle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DOC_TYPES, docTypeLabel } from "../lib/constants";
import { Plus, Trash } from "@phosphor-icons/react";

function CoverageMatrix({ nav }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [openKey, setOpenKey] = useState(null);
  const [tab, setTab] = useState("mapped");
  useEffect(() => {
    api.get("/documents/coverage").then((r) => setData(r.data)).catch(() => setData({ requirements: [], document_count: 0, gaps: [], applicable_count: 0, covered_count: 0 }));
  }, []);
  if (!data) return <div className="p-4 text-sm text-muted-foreground">Loading coverage…</div>;
  const q = filter.toLowerCase();
  const reqs = data.requirements.filter((r) => !filter || `${r.standard_id} ${r.req_number}`.toLowerCase().includes(q));
  const gaps = (data.gaps || []).filter((r) => !filter || `${r.standard_id} ${r.req_number}`.toLowerCase().includes(q));
  const pct = data.applicable_count ? Math.round((data.covered_count / data.applicable_count) * 100) : 0;
  return (
    <div className="border border-border bg-card" data-testid="coverage-matrix">
      <div className="px-4 py-3 border-b border-border grid gap-3 sm:grid-cols-3 items-center">
        <div className="flex items-center gap-4 text-xs font-mono">
          <span data-testid="coverage-covered"><b className="text-green-700">{data.covered_count}</b> covered</span>
          <span data-testid="coverage-gapcount"><b className="text-red-600">{(data.gaps || []).length}</b> gaps</span>
          <span className="text-muted-foreground">of {data.applicable_count} applicable</span>
        </div>
        <div className="sm:col-span-2 flex items-center gap-2">
          <div className="flex-1 h-2 bg-secondary/60 overflow-hidden" title={`${pct}% of applicable requirements addressed`}>
            <div className="h-full bg-green-600" style={{ width: `${pct}%` }} data-testid="coverage-progress" />
          </div>
          <span className="text-xs font-mono text-muted-foreground w-10 text-right">{pct}%</span>
        </div>
      </div>
      <div className="px-4 py-2 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex border border-border" data-testid="coverage-subtabs">
          <button type="button" data-testid="coverage-tab-mapped" onClick={() => setTab("mapped")}
            className={`px-3 py-1 text-xs font-semibold ${tab === "mapped" ? "bg-primary text-primary-foreground" : "hover:bg-secondary/40"}`}>Mapped ({data.requirements.length})</button>
          <button type="button" data-testid="coverage-tab-gaps" onClick={() => setTab("gaps")}
            className={`px-3 py-1 text-xs font-semibold border-l border-border ${tab === "gaps" ? "bg-red-600 text-white" : "hover:bg-secondary/40"}`}>Gaps ({(data.gaps || []).length})</button>
        </div>
        <Input data-testid="coverage-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter… e.g. CIP-007 R1" className="rounded-none h-8 text-sm max-w-xs" />
      </div>

      {tab === "gaps" ? (
        gaps.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-green-700">No coverage gaps — every applicable requirement is addressed by at least one document. 🎉</div>
        ) : (
          <div className="p-3 flex flex-wrap gap-1.5" data-testid="coverage-gaps-list">
            {gaps.map((r, i) => (
              <span key={i} data-testid={`coverage-gap-${r.standard_id}-${r.req_number}`} title={r.standard_title}
                className="inline-flex items-center gap-1 border border-red-300 bg-red-50 text-red-700 px-2 py-1 text-xs font-mono">
                {r.standard_id} {r.req_number}
              </span>
            ))}
          </div>
        )
      ) : reqs.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-muted-foreground">No requirement mappings yet. Open a document and tag each section with the Standard / Requirement / Part it addresses.</div>
      ) : (
        <div className="divide-y divide-border">
          {reqs.map((r) => {
            const key = `${r.standard_id}|${r.req_number}`;
            const open = openKey === key;
            return (
              <div key={key} data-testid={`coverage-req-${r.standard_id}-${r.req_number}`}>
                <button type="button" onClick={() => setOpenKey(open ? null : key)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-secondary/40 text-left">
                  <span className="min-w-0">
                    <span className="font-mono font-bold">{r.standard_id} {r.req_number}</span>
                    {r.parts.length > 0 && <span className="text-xs text-muted-foreground ml-2">{r.parts.length === 1 ? "part" : "parts"} {r.parts.join(", ")}</span>}
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-mono border border-border px-2 py-0.5 bg-secondary/40">{r.doc_count} doc{r.doc_count === 1 ? "" : "s"}</span>
                    <span className="text-muted-foreground text-xs">{open ? "▲" : "▼"}</span>
                  </span>
                </button>
                {open && (
                  <div className="px-4 pb-3 space-y-1.5" data-testid={`coverage-docs-${r.standard_id}-${r.req_number}`}>
                    {r.documents.map((dd, idx) => (
                      <button key={idx} onClick={() => nav(`/documents/${dd.id}`)}
                        className="w-full flex items-center justify-between gap-2 border border-border px-3 py-2 hover:bg-secondary/40 text-left">
                        <span className="min-w-0">
                          <span className="font-medium">{dd.title}</span>
                          <span className="text-xs text-muted-foreground ml-2 capitalize">{docTypeLabel(dd.doc_type)}</span>
                          <span className="text-xs text-muted-foreground ml-2">§{dd.section_number} {dd.section_title}</span>
                          {dd.parts.length > 0 && <span className="text-xs text-muted-foreground ml-2">· {dd.parts.length === 1 ? "part" : "parts"} {dd.parts.join(", ")}</span>}
                        </span>
                        <StatusBadge status={dd.status} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("policy");
  const [mode, setMode] = useState("docs");
  const { canEdit, isRole } = useAuth();
  const isAdmin = isRole("admin");
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const family = sp.get("family") || "";
  const status = sp.get("status") || "";
  const { isSubHidden } = useModuleVisibility();
  // Doc-type submodule gates belong to the `documents` module only (not the CIP
  // Document Library or Proposed 5 P's views, which share this page).
  const applyDocTypeGate = family !== "cip" && status !== "draft";
  const docTypeOptions = applyDocTypeGate ? DOC_TYPES.filter((t) => !isSubHidden(`documents:${t.value}`)) : DOC_TYPES;
  const visibleDocs = applyDocTypeGate ? docs.filter((d) => !isSubHidden(`documents:${d.doc_type}`)) : docs;
  useEffect(() => {
    if (applyDocTypeGate && isSubHidden(`documents:${docType}`)) setDocType(docTypeOptions[0]?.value || "policy");
  }, [docType, applyDocTypeGate, docTypeOptions, isSubHidden]);
  const view = family === "cip" ? { title: "CIP Document Library", sub: "CIP-specific policies, procedures, plans, programs, processes & other controls" }
    : family === "op" ? { title: "Documents (5 P's)", sub: "O&P policies, plans, procedures & processes" }
    : status === "draft" ? { title: "Proposed 5 P's", sub: "Draft / future-readiness documents" }
    : { title: "Documents", sub: "Policies, plans, procedures & processes" };

  const load = useCallback(() => {
    const qs = new URLSearchParams();
    if (family) qs.set("family", family);
    if (status) qs.set("status", status);
    const q = qs.toString();
    return api.get(`/documents${q ? `?${q}` : ""}`).then((r) => setDocs(r.data));
  }, [family, status]);
  useEffect(() => { load(); }, [load]);

  const del = async (e, d) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${d.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Document deleted");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const create = async () => {
    if (!title.trim()) return;
    try {
      const { data } = await api.post("/documents", {
        title, doc_type: docType,
        sections: [{ id: crypto.randomUUID(), title: "Purpose", content: "" }, { id: crypto.randomUUID(), title: "Scope", content: "" }],
        linked_requirements: [],
      });
      setOpen(false); setTitle("");
      nav(`/documents/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title={view.title} subtitle={view.sub}
        action={
          <div className="flex items-center gap-3">
            <FamilyToggle testid="documents-family-toggle" />
            {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-document-btn" className="rounded-none bg-primary hover:bg-primary/90"><Plus size={16} className="mr-1" />New Document</Button></DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader><DialogTitle>New Document</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Title</Label><Input data-testid="doc-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-none mt-1" /></div>
                <div><Label>Type</Label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger data-testid="doc-type-select" className="rounded-none mt-1 capitalize"><SelectValue /></SelectTrigger>
                    <SelectContent>{docTypeOptions.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button data-testid="create-document-btn" onClick={create} className="rounded-none w-full bg-primary hover:bg-primary/90">Create & Edit</Button>
              </div>
            </DialogContent>
          </Dialog>
            )}
          </div>
        }
      />
      <div className="p-8">
        <DataResidencyNote className="mb-4" />
        <div className="mb-4 inline-flex border border-border" data-testid="documents-view-toggle">
          <button type="button" data-testid="view-documents-btn" onClick={() => setMode("docs")}
            className={`px-4 py-1.5 text-sm font-semibold ${mode === "docs" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-secondary/40"}`}>Documents</button>
          <button type="button" data-testid="view-coverage-btn" onClick={() => setMode("coverage")}
            className={`px-4 py-1.5 text-sm font-semibold border-l border-border ${mode === "coverage" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-secondary/40"}`}>Coverage</button>
        </div>
        {mode === "coverage" ? <CoverageMatrix nav={nav} /> : visibleDocs.length === 0 ? <Empty title="No documents yet" hint="Create your first compliance document." /> : (
          <div className="border border-border bg-card">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
                <th className="px-4 py-3">Title</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Version</th><th className="px-4 py-3">Owner</th>{isAdmin && <th className="px-4 py-3 w-10"></th>}
              </tr></thead>
              <tbody>
                {visibleDocs.map((d) => (
                  <tr key={d.id} data-testid={`doc-row-${d.id}`} onClick={() => nav(`/documents/${d.id}`)} className="border-b border-border hover:bg-secondary/40 cursor-pointer">
                    <td className="px-4 py-3 font-medium">{d.title}</td>
                    <td className="px-4 py-3 capitalize">{docTypeLabel(d.doc_type)}</td>
                    <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                    <td className="px-4 py-3 font-mono">v{d.version}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.owner}</td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <button data-testid={`doc-delete-${d.id}`} onClick={(e) => del(e, d)} title="Delete document"
                          className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
